import {useState} from 'react';
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card.tsx';
import {Button} from '@/components/ui/button.tsx';
import {Badge} from '@/components/ui/badge.tsx';
import {PencilIcon} from '@heroicons/react/24/outline';
import {CustomerDetails} from '@/store/customerFile.ts';
import {EditBillingModal} from './EditBillingModal.tsx';
import {format} from 'date-fns';

interface Props {
    customer: CustomerDetails;
    canEdit: boolean;
}

function PaymentTypeLabel({type}: {type: number}) {
    const variants: Record<number, {label: string; className: string}> = {
        0: {label: 'לא הוגדר', className: 'bg-gray-100 text-gray-600'},
        1: {label: 'אשראי', className: 'bg-blue-100 text-blue-700'},
        2: {label: 'הוראת קבע', className: 'bg-emerald-100 text-emerald-700'},
        3: {label: 'העברה בנקאית', className: 'bg-violet-100 text-violet-700'},
        4: {label: 'מזומן', className: 'bg-amber-100 text-amber-700'},
        5: {label: 'שיק', className: 'bg-orange-100 text-orange-700'},
        6: {label: 'לא פעיל', className: 'bg-red-100 text-red-600'},
        7: {label: 'אשראי', className: 'bg-blue-100 text-blue-700'},
    };
    const v = variants[type] ?? {label: String(type), className: 'bg-gray-100 text-gray-600'};
    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${v.className}`}>
            {v.label}
        </span>
    );
}

function InfoRow({label, value}: {label: string; value?: React.ReactNode | null}) {
    if (!value) return null;
    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
            <span className="text-xs text-gray-400 w-32 shrink-0">{label}</span>
            <span className="text-sm text-gray-800 font-medium">{value}</span>
        </div>
    );
}

function isCreditType(type: number) {
    return type === 1 || type === 7;
}

function isDirectDebitType(type: number) {
    return type === 2;
}

export function CustomerFileBillingCard({customer, canEdit}: Props) {
    const [editOpen, setEditOpen] = useState(false);

    return (
        <>
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-base">פרטי חיוב ותשלום</CardTitle>
                        {canEdit && (
                            <Button variant="ghost" size="icon" onClick={() => setEditOpen(true)}>
                                <PencilIcon className="w-4 h-4" />
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="space-y-2.5">
                    <InfoRow
                        label="אמצעי תשלום"
                        value={<PaymentTypeLabel type={customer.payment_type} />}
                    />
                    <InfoRow
                        label="יום חיוב"
                        value={`${customer.payment_day} לחודש`}
                    />

                    {isCreditType(customer.payment_type) && (
                        <>
                            <InfoRow label="4 ספרות אחרונות" value={customer.last_4_digits ? `**** ${customer.last_4_digits}` : null} />
                            <InfoRow label="מותג" value={customer.brand} />
                            <InfoRow
                                label="תוקף"
                                value={
                                    customer.expiration
                                        ? format(new Date(customer.expiration), 'MM/yy')
                                        : null
                                }
                            />
                        </>
                    )}

                    {isDirectDebitType(customer.payment_type) && (
                        <>
                            <InfoRow label='מזהה הו"ק' value={customer.nedarim_hok_id} />
                            <InfoRow label="בנק" value={customer.bank ? String(customer.bank) : null} />
                            <InfoRow label="סניף" value={customer.branch ? String(customer.branch) : null} />
                            <InfoRow label="חשבון" value={customer.account ? String(customer.account) : null} />
                        </>
                    )}
                </CardContent>
            </Card>

            {editOpen && (
                <EditBillingModal
                    open={editOpen}
                    onClose={() => setEditOpen(false)}
                    customer={customer}
                />
            )}
        </>
    );
}
